import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {AgentsHttpService} from "../../../../agents/angular/services/agents-http.service";
import {AgentModel} from "../../../../agents/angular/models/agent.model";
import {VisitPlanHttpService} from "../../services/visit-plan-http.service";
import {VisitScheduleResourceModel} from "../../../models/visit-schedule-resource.model";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {CommonsFacadeService} from "../../../../commons/angular/state/commons/commons-facade.service";
import {AvailabilityScheduleType} from "../../../enums/availability-schedule-type.enum";
import * as moment from 'moment-timezone';
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {VisitScheduleResourceHoldersParamsModel} from "../../../models/visit-schedule-resource-holders-params.model";
import {ResourceHoldersPaginationDataModel} from "../../../models/resource-holders-pagination-data.model";
import * as _ from "lodash";
import {VisitPlanFacadeService} from "../../services/visit-plan-facade.service";
import {VisitScheduleResourceHolderExt} from "../../../models/visit-schedule-resource-holder-ext.model";
import {GroupsHttpService} from "../../../../groups/angular/services/groups-http.service";

require('./availability-table.scss');
@Component({
  standalone: false,
  selector: 'app-availability-table',
  templateUrl: './availability-table.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvailabilityTableComponent implements OnInit, AfterViewInit, DoCheck, OnChanges, OnDestroy {

  @Input() visitPlan?: VisitPlanModel;
  @Input() visitScheduleId?: string;
  @Input() branchIds?: string[];
  @Input() from?: number;
  @Input() to?: number;
  @Input() zoneId?: string;
  @Input() searchQuery?: string;
  @Input() filterEmployeeIds?: string[];
  @Input() filterOperationalUnitIds?: string[];
  @Input() filterTerritoryIds?: string[];
  @Input() filterTeamIds?: string[];
  @Input() filterTags?: string[];
  @Input() filterExternal?: boolean;
  @Input() reload?: boolean;

  public visitPlanId: string;
  public agents: VisitScheduleResourceHolderExt[] = [];
  public resources: VisitScheduleResourceModel[] = [];
  public resourcesByAgent: Map<string, Map<string, VisitScheduleResourceModel[]>> = new Map();
  public conflictingResourceIds: Set<string> = new Set();
  public dates: string[] = [];
  public dateInfo: Map<string, { dayName: string; formattedDate: string }> = new Map();
  public cursor?: string;
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public totalsColumnWidth: number = 460;
  public onWindowResizeBound: any;
  private destroy$ = new Subject<void>();

  @Output() onFetchNext = new EventEmitter<null>();
  @Output() onInstanceReady = new EventEmitter<any>();
  @Output() onResourcesUpdate = new EventEmitter<VisitScheduleResourceModel[]>();

  @ViewChild(CdkVirtualScrollViewport, {static: false})
  private cdkVirtualScrollViewport: CdkVirtualScrollViewport;

  constructor(
    @Inject(ElementRef) private elRef: ElementRef,
    @Inject(VisitPlanFacadeService) private visitPlanFacadeService: VisitPlanFacadeService,
    @Inject(AgentsHttpService) private agentsHttpService: AgentsHttpService,
    @Inject(VisitPlanHttpService) private visitPlanHttpService: VisitPlanHttpService,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(CommonsFacadeService) private commonsFacadeService: CommonsFacadeService,
    @Inject(GroupsHttpService) private groupsHttpService: GroupsHttpService,
    @Inject('schedulingProjectAlertsService') private schedulingProjectAlertsService: any
  ) {
  }

  get isInitialLoading(): boolean {
    return this.isLoading && !this.isInitialized;
  }

  ngOnInit() {
    this.visitPlanId = this.visitPlan?.id;
    this.generateDates();
    this.loadAgents();

    // Add window resize listener
    this.onWindowResizeBound = this.setTotalsWidth.bind(this);
    window.addEventListener('resize', this.onWindowResizeBound);

    // Emit instance for AngularJS dialog
    this.onInstanceReady.emit(this);
  }

  ngAfterViewInit(): void {

    const div1 = document.getElementById('header-availability-table') as HTMLElement;
    const div2 = document.querySelectorAll('.cdk-availability')[0];

    div2.addEventListener('scroll', e => {
      div1.scrollTop = div2.scrollTop;
      div1.scrollLeft = div2.scrollLeft;
    })
    // this.cdkVirtualScrollViewport?.checkViewportSize();
  }

  ngDoCheck() {
    this.cdkVirtualScrollViewport?.checkViewportSize();
  }

  ngOnChanges(changes: SimpleChanges) {
    const filterProperties = ['searchQuery', 'filterEmployeeIds', 'filterOperationalUnitIds', 'filterTerritoryIds', 'filterTeamIds', 'filterTags', 'filterExternal'];
    const hasFilterChange = filterProperties.some(prop => changes[prop] && !changes[prop].firstChange);
    const doReload = changes.reload && !changes.reload.isFirstChange() && !!this.reload;

    if (hasFilterChange || doReload) {
      // Reset and reload when any filter changes
      this.agents = [];
      this.resources = [];
      this.resourcesByAgent = new Map();
      this.conflictingResourceIds = new Set();
      this.cursor = undefined;
      this.isInitialized = false;
      this.loadAgents();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', this.onWindowResizeBound);
  }

  private generateDates() {
    // Use from/to timestamps instead of today
    if (!this.from || !this.to) {
      // Fallback to 7 days if from/to not provided
      const today = new Date();
      this.dates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        this.dates.push(date.toISOString().split('T')[0]);
      }
      this._computeDateInfo();
      return;
    }

    this.dates = [];

    // Use moment-timezone to properly handle dates in the visit plan's timezone
    const timezone = this.zoneId || moment.tz.guess();

    const startMoment = moment.tz(this.from, timezone).startOf('day');
    const endMoment = moment.tz(this.to, timezone).endOf('day');

    // Generate dates
    let currentMoment = startMoment.clone();

    while (currentMoment.isSameOrBefore(endMoment, 'day')) {
      const dateStr = currentMoment.format('YYYY-MM-DD');
      this.dates.push(dateStr);
      currentMoment.add(1, 'day');
    }
    this._computeDateInfo();
  }

  private _computeDateInfo() {
    const info = new Map();
    this.dates.forEach(dateStr => {
      const dayName = this._getDayName(dateStr);
      const formattedDate = this._getFormattedDate(dateStr);
      info.set(dateStr, { dayName, formattedDate });
    });
    this.dateInfo = info;
  }

  private loadAgents() {
    if (this.isLoading) return;

    this.isLoading = true;
    if (!this.isInitialized) {
      this.commonsFacadeService.setLoaderOn();
    }
    this.cdr.markForCheck();

    if (this.filterTeamIds && this.filterTeamIds.length > 0) {
      this.groupsHttpService.getGroups({ ids: this.filterTeamIds })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const groupBranchIds = _.compact(_.uniq(_.flatMap(response.list || [], 'branchIds')));
            this.fetchResourceHolders(groupBranchIds);
          },
          error: (error) => {
            console.error('Error loading groups:', error);
            this.fetchResourceHolders();
          }
        });
    } else {
      this.fetchResourceHolders();
    }
  }

  private fetchResourceHolders(groupBranchIds: string[] = []) {
    const params: any = this._getResourceHoldersQuery();

    if (groupBranchIds.length > 0) {
       params.projectBranchIds = _.uniq([...(params.projectBranchIds || []), ...groupBranchIds]);
    }

    this.visitPlanFacadeService.getResourceHoldersWithCursor(this.visitPlan.id, this.visitPlan.scheduleId, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (this.cursor) {
            // Append new agents to existing list
            this.agents = [...this.agents, ...response.list];
          } else {
            // First load
            this.agents = response.list;
          }
          this.cursor = response.cursor;

          // After loading agents, fetch resources for these agents
          this.loadResourcesForAgents(response.list, () => {
            this.isLoading = false;
            this.isInitialized = true;
            this.commonsFacadeService.setLoaderOff();
            this.cdr.markForCheck();
            // Check viewport size after data loads
            setTimeout(() => {
              this.cdkVirtualScrollViewport?.checkViewportSize();
            }, 0);
          });
        },
        error: (error) => {
          console.error('Error loading agents:', error);
          this.isLoading = false;
          this.isInitialized = true;
          this.commonsFacadeService.setLoaderOff();
          this.cdr.markForCheck();
        }
      });
  }

  private loadResourcesForAgents(resourceHolders: VisitScheduleResourceHolderExt[], onComplete?: () => void) {
    if (!this.visitPlanId || !this.visitScheduleId || !resourceHolders.length) {
      if (onComplete) {
        onComplete();
      }
      return;
    }

    const resourceHolderIds = resourceHolders.map(r => r.id);
    const resourceQuery = this._buildResourceQuery(resourceHolderIds);

    this.visitPlanHttpService.getResourcesList(this.visitPlanId, this.visitScheduleId, resourceQuery)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resources) => {
          this.resources = [...this.resources, ...resources];
          // Organize resources by agent and date
          this.organizeResourcesByAgent();
          this.cdr.markForCheck();
          if (onComplete) {
            onComplete();
          }
        },
        error: (error) => {
          console.error('Error loading resources:', error);
          if (onComplete) {
            onComplete();
          }
        }
      });

    this.loadConflicts(resourceHolderIds);
  }

  private _buildResourceQuery(resourceHolderIds: string[]): any {
    return {
      from: this.from,
      to: this.to,
      resourceHolderIds: resourceHolderIds
    };
  }

  private loadConflicts(resourceHolderIds: string[]) {
    if (!this.visitPlanId || !this.visitScheduleId || !resourceHolderIds.length) return;

    const query = this._buildResourceQuery(resourceHolderIds);
    this.visitPlanHttpService.getResourceConflicts(this.visitPlanId, this.visitScheduleId, query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (conflictIds) => {
          const scopedIds = new Set(
            this.resources.filter(r => resourceHolderIds.includes(r.resourceHolderId)).map(r => r.id)
          );
          const next = new Set(this.conflictingResourceIds);
          scopedIds.forEach(id => next.delete(id));
          conflictIds.forEach(id => next.add(id));
          this.conflictingResourceIds = next;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading resource conflicts:', error);
        }
      });
  }

  private organizeResourcesByAgent() {
    this.resourcesByAgent = new Map();

    this.resources.forEach(resource => {
      // Only organize resources with scheduleType "Availability" or "Leave"
      const scheduleTypeValue = resource.scheduleType as string;
      if (scheduleTypeValue !== AvailabilityScheduleType.Availability && scheduleTypeValue !== AvailabilityScheduleType.Leave) {
        return;
      }

      // Get agent ID
      const resourceHolderId = resource.resourceHolderId;

      // Extract date from visitScheduleDayId (format: "10-11-2025_...")
      const dayId = resource.visitScheduleDayId;
      const datePart = dayId.split('_')[0]; // Gets "10-11-2025"
      const [day, month, year] = datePart.split('-');
      const dateKey = `${year}-${month}-${day}`; // Convert to "2025-11-10"

      // Create nested map structure
      if (!this.resourcesByAgent.has(resourceHolderId)) {
        this.resourcesByAgent.set(resourceHolderId, new Map());
      }

      const agentResourceMap = this.resourcesByAgent.get(resourceHolderId)!;
      if (!agentResourceMap.has(dateKey)) {
        agentResourceMap.set(dateKey, []);
      }

      agentResourceMap.get(dateKey)!.push(resource);
    });
  }

  getNextBatch() {
    if (this.isLoading || !this.cursor) return;

    if (this.cdkVirtualScrollViewport.measureScrollOffset('bottom') < 50) {
      this.loadAgents();
    }
  }

  private _getDayName(dateString: string): string {
    const timezone = this.zoneId || moment.tz.guess();
    const dateMoment = moment.tz(dateString, timezone);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dateMoment.day()];
  }

  private _getFormattedDate(dateString: string): string {
    const timezone = this.zoneId || moment.tz.guess();
    const dateMoment = moment.tz(dateString, timezone);
    const month = (dateMoment.month() + 1).toString().padStart(2, '0');
    const day = dateMoment.date().toString().padStart(2, '0');
    return `${month}/${day}`;
  }

  setTotalsWidth(width?: number): void {
    const sticky = this.elRef.nativeElement.querySelector('.sticky') as HTMLElement;
    this.totalsColumnWidth = width || sticky.offsetWidth;
  }

  onResourcesUpdated(updatedResources: VisitScheduleResourceModel[]) {
    if (!updatedResources || !updatedResources.length) {
      return;
    }

    const updatedMap = new Map(updatedResources.filter(r => !!r.id).map(r => [r.id, r]));

    this.resources = this.resources.map(r => {
      if (r.id && updatedMap.has(r.id)) {
        const updated = updatedMap.get(r.id);
        updatedMap.delete(r.id);
        return updated!;
      }
      return r;
    });

    // Append any new resources (remaining in map)
    if (updatedMap.size > 0) {
      this.resources = [...this.resources, ...Array.from(updatedMap.values())];
    }

    // Re-organize resources
    this.organizeResourcesByAgent();
    this.cdr.markForCheck();

    // Conflicts are never recomputed synchronously by create/update — refresh markers for the affected agents
    const affectedResourceHolderIds = _.uniq(updatedResources.map(r => r.resourceHolderId).filter(Boolean));
    this.loadConflicts(affectedResourceHolderIds);

    // Emit updated resources to parent (dialog/view)
    this.onResourcesUpdate.emit(updatedResources);
  }

  onDisableAllDays(holderId: string) {
    if (!this.visitPlanId || !this.visitScheduleId) return;

    const agentResources = this.resourcesByAgent.get(holderId);
    if (!agentResources) return;

    const resourcesToUpdate: any[] = [];
    agentResources.forEach((dateResources) => {
      dateResources
        .filter(r => r.scheduleType === 'Availability' && !r.disabled)
        .forEach(r => {
          const resourceCopy = {...r};
          resourceCopy.disabled = true;
          resourcesToUpdate.push(resourceCopy);
        });
    });

    if (!resourcesToUpdate.length) return;

    this.commonsFacadeService.setLoaderOn();
    this.visitPlanHttpService.editResources(this.visitPlanId, this.visitScheduleId, resourcesToUpdate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedResources) => {
          this.commonsFacadeService.setLoaderOff();
          this.onResourcesUpdated(updatedResources);
        },
        error: (error) => {
          this.commonsFacadeService.setLoaderOff();
          if (error?.error?.code === 400468) {
            this.schedulingProjectAlertsService.editResourcesSkillRequiredError();
          }
        }
      });
  }

  submitResources(): Promise<VisitScheduleResourceModel[]> {
    return new Promise((resolve, reject) => {
      if (!this.visitPlanId || !this.visitScheduleId) {
        reject(new Error('Visit Plan ID or Visit Schedule ID is missing'));
        return;
      }

      this.commonsFacadeService.setLoaderOn();

      this.visitPlanHttpService.submitResources(this.visitPlanId, this.visitScheduleId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (submittedResources) => {
            this.commonsFacadeService.setLoaderOff();
            // Update local resources with submitted data
            this.resources = [...this.resources, ...submittedResources];
            this.organizeResourcesByAgent();
            this.cdr.markForCheck();
            resolve(submittedResources);
          },
          error: (error) => {
            console.error('Error submitting resources:', error);
            this.commonsFacadeService.setLoaderOff();
            reject(error);
          }
        });
    });
  }

  _getResourceHoldersQuery() {
    const params: any = { limit: 20, sort :  "holderName,asc"};
    if (this.cursor) {
      params.cursor = this.cursor;
    }
    // Use operational unit filter if set, otherwise use branchIds
    if (this.filterOperationalUnitIds && this.filterOperationalUnitIds.length > 0) {
      params.projectBranchIds = [...this.filterOperationalUnitIds];
    } else if (this.branchIds) {
      params.projectBranchIds = [...this.branchIds];
    }

    if (this.searchQuery && this.searchQuery.trim()) {
      params.query = this.searchQuery.trim();
    }

    if (this.filterEmployeeIds && this.filterEmployeeIds.length > 0) {
      params.holderIds = this.filterEmployeeIds;
    }
    if (this.filterTerritoryIds && this.filterTerritoryIds.length > 0) {
      params.areaIds = this.filterTerritoryIds;
    }
    if (this.filterTags && this.filterTags.length > 0) {
      params.agentTags = this.filterTags;
    }
    if (_.isBoolean(this.filterExternal)) {
      params.external = this.filterExternal;
    }

    return params;
  }

}
